import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity,
  CheckSquare,
  ChevronRight,
  ExternalLink,
  MessageSquare,
  X,
} from "lucide-react";

const AUTO_KEY = (boardId) => `kanbn:agenda-auto:${boardId}`;

export function isAgendaAutoOpen(boardId) {
  try {
    return localStorage.getItem(AUTO_KEY(boardId)) !== "off";
  } catch {
    return true;
  }
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AgendaDialog({
  open,
  onOpenChange,
  boardId,
  onOpenCard,
}) {
  const [loading, setLoading] = useState(false);
  const [myCards, setMyCards] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [comment, setComment] = useState("");
  const [autoOpen, setAutoOpen] = useState(() => isAgendaAutoOpen(boardId));

  const load = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/boards/${boardId}/agenda`);
      setMyCards(data.my_cards || []);
      setRecentActivity(data.recent_activity || []);
      if (!selectedId && data.my_cards && data.my_cards.length) {
        setSelectedId(data.my_cards[0].card.id);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load agenda");
    } finally {
      setLoading(false);
    }
  }, [boardId, selectedId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const selected = useMemo(
    () => myCards.find((m) => m.card.id === selectedId) || null,
    [myCards, selectedId]
  );

  const toggleChecklist = async (itemId) => {
    if (!selected) return;
    const updated = (selected.card.checklist || []).map((it) =>
      it.id === itemId ? { ...it, done: !it.done } : it
    );
    // optimistic
    setMyCards((cs) =>
      cs.map((m) =>
        m.card.id === selected.card.id
          ? {
              ...m,
              card: { ...m.card, checklist: updated },
              checklist_done: updated.filter((i) => i.done).length,
              checklist_total: updated.length,
            }
          : m
      )
    );
    try {
      await api.patch(`/cards/${selected.card.id}`, { checklist: updated });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
      load();
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const t = comment.trim();
    if (!t || !selected) return;
    try {
      const { data } = await api.post(`/cards/${selected.card.id}/comments`, { text: t });
      setMyCards((cs) =>
        cs.map((m) =>
          m.card.id === selected.card.id
            ? {
                ...m,
                recent_comments: [...m.recent_comments, data].slice(-3),
                last_activity_at: data.created_at,
              }
            : m
        )
      );
      setComment("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const openFull = () => {
    if (!selected) return;
    onOpenChange(false);
    onOpenCard && onOpenCard(selected.card.id);
  };

  const toggleAutoOpen = (e) => {
    const next = e.target.checked;
    setAutoOpen(next);
    try {
      localStorage.setItem(AUTO_KEY(boardId), next ? "on" : "off");
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none sm:max-w-4xl max-w-full w-full sm:w-auto h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden p-0 inset-0 sm:inset-auto translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%] top-0 left-0 sm:top-1/2 sm:left-1/2 flex flex-col"
        data-testid="agenda-dialog"
      >
        <DialogHeader className="p-4 sm:p-6 pb-3 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
          <DialogTitle className="font-display text-xl sm:text-2xl font-bold tracking-tight">
            Your agenda
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 font-mono-tight uppercase tracking-widest">
            {loading
              ? "Loading..."
              : `${myCards.length} card${myCards.length === 1 ? "" : "s"} need your attention`}
          </p>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[260px_1fr] min-h-0 overflow-hidden">
          {/* Left — my cards list */}
          <aside className="border-b md:border-b-0 md:border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto max-h-[40vh] md:max-h-none">
            {!loading && myCards.length === 0 && (
              <div className="p-6 text-center text-sm text-zinc-500">
                Nothing on your plate here. Cards you create or comment on will show up here.
              </div>
            )}
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
              {myCards.map((m) => {
                const active = m.card.id === selectedId;
                const total = m.checklist_total;
                const done = m.checklist_done;
                return (
                  <li key={m.card.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(m.card.id)}
                      className={`w-full text-left px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-zinc-100 dark:bg-zinc-800/60"
                          : "hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
                      }`}
                      data-testid={`agenda-card-${m.card.id}`}
                    >
                      <div className="text-sm font-medium text-zinc-950 dark:text-zinc-100 line-clamp-2">
                        {m.card.title}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] font-mono-tight uppercase tracking-widest text-zinc-500">
                        <span className="truncate">{m.list_title}</span>
                        {total > 0 && (
                          <span
                            className={`inline-flex items-center gap-0.5 ${
                              done === total ? "text-green-600 dark:text-green-400" : ""
                            }`}
                          >
                            <CheckSquare className="h-3 w-3" />
                            {done}/{total}
                          </span>
                        )}
                        {m.recent_comments.length > 0 && (
                          <span className="inline-flex items-center gap-0.5">
                            <MessageSquare className="h-3 w-3" />
                            {m.recent_comments.length}
                          </span>
                        )}
                        <span className="ml-auto">{timeAgo(m.last_activity_at)}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Right — selected card detail */}
          <section className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
            {!selected ? (
              <div className="text-sm text-zinc-500">
                Select a card on the left to see its checklist and recent activity.
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono-tight uppercase tracking-widest text-zinc-500 mb-1">
                      {selected.list_title}
                    </div>
                    <h3 className="font-display text-lg font-bold tracking-tight text-zinc-950 dark:text-zinc-100">
                      {selected.card.title}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={openFull}
                    className="shrink-0 inline-flex items-center gap-1 border border-zinc-200 dark:border-zinc-700 px-2.5 py-1.5 text-xs font-medium hover:border-black dark:hover:border-white transition-colors"
                    data-testid="agenda-open-full"
                  >
                    Open full card <ExternalLink className="h-3 w-3" />
                  </button>
                </div>

                {/* Checklist */}
                <div>
                  <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                    <CheckSquare className="h-3 w-3" /> Needs to be done
                  </div>
                  {(selected.card.checklist || []).length === 0 ? (
                    <div className="text-xs text-zinc-400 italic">
                      No checklist on this card yet.
                    </div>
                  ) : (
                    <ul className="space-y-1.5">
                      {selected.card.checklist.map((it) => (
                        <li key={it.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!it.done}
                            onChange={() => toggleChecklist(it.id)}
                            className="h-4 w-4 accent-black"
                            data-testid={`agenda-check-${it.id}`}
                          />
                          <span
                            className={`text-sm ${
                              it.done ? "line-through text-zinc-400" : "text-zinc-700 dark:text-zinc-300"
                            }`}
                          >
                            {it.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Recent comments */}
                <div>
                  <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                    <MessageSquare className="h-3 w-3" /> Recent comments
                  </div>
                  {selected.recent_comments.length === 0 ? (
                    <div className="text-xs text-zinc-400 italic mb-2">No comments yet.</div>
                  ) : (
                    <ul className="space-y-2 mb-3">
                      {selected.recent_comments.map((c) => (
                        <li
                          key={c.id}
                          className="border-l-2 border-zinc-200 dark:border-zinc-700 pl-3"
                        >
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-medium truncate">{c.user_name}</span>
                            <span className="text-zinc-400 shrink-0">{timeAgo(c.created_at)}</span>
                          </div>
                          <div className="text-sm mt-0.5 text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap line-clamp-3">
                            {c.text}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <form onSubmit={submitComment} className="flex gap-2">
                    <input
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Quick comment..."
                      className="flex-1 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-black dark:focus:border-white bg-transparent text-zinc-950 dark:text-zinc-100"
                      data-testid="agenda-comment-input"
                    />
                    <button
                      type="submit"
                      disabled={!comment.trim()}
                      className="px-3 py-2 text-xs font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-zinc-800 dark:hover:bg-zinc-200 disabled:opacity-50"
                      data-testid="agenda-comment-submit"
                    >
                      Send
                    </button>
                  </form>
                </div>

                {/* Recent board activity */}
                {recentActivity.length > 0 && (
                  <details className="border-t border-zinc-200 dark:border-zinc-800 pt-3">
                    <summary className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 cursor-pointer flex items-center gap-2">
                      <Activity className="h-3 w-3" /> Recent board activity
                      <ChevronRight className="h-3 w-3 ml-auto transition-transform group-open:rotate-90" />
                    </summary>
                    <ul className="mt-2 space-y-1.5">
                      {recentActivity.map((a) => (
                        <li key={a.id} className="text-xs text-zinc-600 dark:text-zinc-400 flex items-start gap-2">
                          <span className="font-medium text-zinc-800 dark:text-zinc-200 shrink-0">
                            {a.user_name}
                          </span>
                          <span className="truncate">{a.action}</span>
                          <span className="ml-auto text-zinc-400 shrink-0">{timeAgo(a.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}
          </section>
        </div>

        <div className="border-t border-zinc-200 dark:border-zinc-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 shrink-0">
          <label className="inline-flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={autoOpen}
              onChange={toggleAutoOpen}
              className="h-4 w-4 accent-black"
              data-testid="agenda-auto-toggle"
            />
            Show automatically when I open this board
          </label>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-zinc-800 dark:hover:bg-zinc-200"
            data-testid="agenda-close"
          >
            <X className="h-3.5 w-3.5" /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
